import { type ButtonHTMLAttributes, forwardRef } from "react";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	loading?: boolean;
	icon?: React.ReactNode;
}

const sizeClasses: Record<ButtonSize, string> = {
	sm: "px-3 py-1.5 text-xs gap-1.5",
	md: "px-4 py-2 text-sm gap-2",
	lg: "px-5 py-2.5 text-base gap-2.5",
};

const variantClasses: Record<ButtonVariant, string> = {
	primary:
		"bg-accent text-bg-primary hover:bg-accent-hover active:bg-accent font-semibold shadow-md hover:shadow-glow",
	secondary:
		"bg-bg-hover text-text-primary border border-border hover:border-border-hover hover:bg-bg-active",
	ghost: "text-text-secondary hover:text-text-primary hover:bg-bg-hover",
	danger:
		"bg-bug/10 text-bug border border-bug/30 hover:bg-bug/20 hover:border-bug/50",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = "primary",
			size = "md",
			loading = false,
			disabled,
			icon,
			children,
			className = "",
			...props
		},
		ref,
	) => {
		return (
			<button
				ref={ref}
				className={`inline-flex items-center justify-center rounded-md font-medium transition-all duration-150 ease-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
				disabled={disabled || loading}
				{...props}
			>
				{loading ? (
					<svg
						className="h-4 w-4 animate-spin"
						viewBox="0 0 24 24"
						fill="none"
						aria-hidden="true"
					>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
						/>
					</svg>
				) : icon ? (
					icon
				) : null}
				{children}
			</button>
		);
	},
);

Button.displayName = "Button";
